import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Loader2, User, Lightbulb } from 'lucide-react';
import { Button } from '../../ui/button';
import { STATUTS_ACTION } from '../constantes';
import { ActionDialog } from '../amelioration/ActionDialog';
import { NIVEAUX } from './niveaux';
import { RisqueDialog, corpsRisque } from './RisqueDialog';
import { MatriceRisques } from './MatriceRisques';
import { BoutonRevue, ListeRevues } from './RevuesRisques';

const CarteRisque = ({ r, categories, onOpen, onAction }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4 transition-[border-color,box-shadow] hover:border-[#8DC63F] hover:shadow-md" data-testid={`risque-${r.risque_id}`}>
    <button type="button" onClick={() => onOpen(r)} className="w-full text-left" data-testid={`risque-ouvrir-${r.risque_id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${NIVEAUX[r.niveau].badge}`} data-testid={`risque-${r.risque_id}-niveau`}>{NIVEAUX[r.niveau].label} · {r.probabilite} × {r.impact}</span>
        <span className="text-xs text-slate-500">{categories[r.categorie]}</span>
      </div>
      <p className="mt-2 text-sm font-semibold text-[#0F172A]">{r.risque}</p>
      {r.mesures && <p className="mt-1 line-clamp-2 text-sm text-slate-500">{r.mesures}</p>}
    </button>
    <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
      {r.responsable ? <span className="flex items-center gap-1"><User className="h-3.5 w-3.5" /> {r.responsable}</span> : <span />}
      {r.action
        ? <a href="/administratif/amelioration-continue" className="rounded-full bg-slate-100 px-2 py-0.5 font-semibold text-slate-700 hover:bg-slate-200" data-testid={`risque-action-${r.risque_id}`}>{r.action.numero} · {STATUTS_ACTION[r.action.statut]?.label}</a>
        : <Button size="sm" variant="ghost" onClick={() => onAction(r)} className="h-7 text-xs text-[#0F172A]" data-testid={`risque-creer-action-${r.risque_id}`}><Lightbulb className="h-3.5 w-3.5 mr-1" /> Créer une action</Button>}
    </div>
  </div>
);

export const CartographieRisques = ({ onChanged }) => {
  const [data, setData] = useState(null);
  const [ouvert, setOuvert] = useState(null);
  const [pourAction, setPourAction] = useState(null);
  const charger = useCallback(() => {
    axios.get('/qualiopi/risques').then((r) => setData(r.data)).catch(() => toast.error('Impossible de charger la cartographie des risques'));
  }, []);
  useEffect(() => { charger(); }, [charger]);
  const apres = () => { setOuvert(null); charger(); onChanged(); };
  const actionCreee = async (action) => {
    const r = pourAction;
    setPourAction(null);
    if (action?.action_id) await axios.put(`/qualiopi/risques/${r.risque_id}`, { ...corpsRisque(r), action_id: action.action_id }).catch(() => toast.error("L'action n'a pas pu être liée au risque"));
    charger(); onChanged();
  };

  if (!data) return <div className="flex justify-center py-12 text-slate-500"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Chargement…</div>;
  const actionDepuis = pourAction && { origine: 'risque', origine_ref: pourAction.risque_id, origine_libelle: `Risque : ${pourAction.risque}`,
    constat: `Risque ${NIVEAUX[pourAction.niveau].label.toLowerCase()} (probabilité ${pourAction.probabilite} × impact ${pourAction.impact}) : ${pourAction.risque}`,
    action: pourAction.mesures || '', indicateurs: [32] };
  return (
    <div className="space-y-4" data-testid="cartographie-risques">
      <p className="text-sm text-slate-500">Indicateur 32 (décret 2026-728) : anticiper ce qui peut nuire à la qualité des formations, prévoir les mesures et revoir la cartographie chaque année.</p>
      <div className="grid gap-4 lg:grid-cols-2">
        <MatriceRisques items={data.items} />
        <ListeRevues revues={data.revues} onChanged={() => { charger(); onChanged(); }} />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm font-semibold text-[#0F172A]" data-testid="risques-compteur">{data.items.length} risque(s) · {data.items.filter((r) => r.niveau === 'eleve').length} élevé(s)</p>
        <div className="flex flex-wrap gap-2">
          <BoutonRevue onDone={() => { charger(); onChanged(); }} />
          <Button onClick={() => setOuvert({})} className="bg-[#0F172A] text-white hover:bg-[#1e293b]" data-testid="risque-nouveau"><Plus className="h-4 w-4 mr-1" /> Nouveau risque</Button>
        </div>
      </div>
      <div className="grid gap-3 lg:grid-cols-2">{data.items.map((r) => <CarteRisque key={r.risque_id} r={r} categories={data.categories} onOpen={setOuvert} onAction={setPourAction} />)}</div>
      <RisqueDialog risque={ouvert} categories={data.categories} onClose={() => setOuvert(null)} onSaved={apres} />
      <ActionDialog action={actionDepuis} onClose={() => setPourAction(null)} onSaved={actionCreee} />
    </div>
  );
};
